<template>
  <main-layout>
    <base-breadcrumb
      :loading="loading"
      :routes="routes"
    />
    <base-widget class="pb-5 main-widget">
      <!-- heading -->
      <div class="row sl-card">
        <out-reach-heading
          :item="stats"
          :loading="loading"
          class="no-border col mr-0"
        />
        <!-- event summary -->
        <events-summary
          :class="`${events.length === 4 ? 'col-12 mt-4' : 'col-'+ (events.length + 1)}`"
          :loading="loading"
          :outreach-id="outreachId"
          :events="events"
          @click="onActivityClicked"
        />
      </div>
      <!-- plots -->
      <out-reach-email-graphs-container
        v-if="display === EDIT_DISPLAY.EMAIL && !customerId"
        class="sl-card"
      />
      <!-- products clicked carousel -->
      <products-clicked
        v-if="stats.products_clicked && stats.products_clicked.length"
        :items="stats.products_clicked"
      />
      <base-card v-if="previewContent">
        <edit-preview
          :content="previewContent"
        />
      </base-card>
      <base-skeleton v-else class="skeleton-preview">
        <div style="display: flex; column-gap: 50px; margin: 50px;">
          <div
            class="skeleton__item"
            style="height: 100px"
          />
        </div>

        <div style="display: flex; column-gap: 50px; margin: 50px;">
          <div
            v-for="n in 3"
            :key="`skeleton_${n}`"
            class="skeleton__item mt-2"
            style="height: 300px"
          />
        </div>
      </base-skeleton>
    </base-widget>
  </main-layout>
</template>
<script>
import OutReachHeading from '@/components/Widgets/OutReachs/OutReachHeading';
import EventsSummary from '@/components/Widgets/OutReachs/EventsSummary';
import OutReachEmailGraphsContainer from '@/components/Widgets/OutReachs/OutReachEmailGraphsContainer';
import ProductsClicked from '@/components/Widgets/OutReachs/ProductsClicked';

import integrable from '@/components/mixins/integrable';
import { EDIT_DISPLAY, EMAIL_EVENTS } from '@/_utilities/Enums';

export default {
  name: 'Stats',
  components: {
    OutReachHeading,
    EventsSummary,
    OutReachEmailGraphsContainer,
    ProductsClicked,
  },
  mixins: [integrable],
  data() {
    return {
      EDIT_DISPLAY,
    };
  },
  computed: {
    editStats() {
      return this.$store.state.outreachs.stats.edit;
    },
    /** Customer specific stats. If undefined, aggregated stats. */
    customerId() {
      return this.$route.params.customerId;
    },
    routes() {
      const customer = this.$store.state.customers.item;
      // Remember: <base-breadcrumb> already manages /admin prepends
      return [
        {
          name: this.customerId ? 'Clients' : 'Reports',
          link: this.customerId ? '/clients' : '/reports',
        }, ...(
          this.customerId ? [{
            name: `${customer.display_name_array[0]}`,
            link: `/clients/${this.customerId}`,
          }] : []),
        {
          name: (this.editStats?.subject?.trim()?.length && this.editStats?.subject)
          || 'Untitled',
        },
      ];
    },
    outreachId() {
      return this.$route.params.id;
    },
    stats() {
      return this.$store.state.outreachs.stats;
    },
    loading() {
      return this.$store.state.outreachs.statsLoading;
    },
    display() {
      return this.stats.edit.display;
    },
    events() {
      const insertIf = (condition, element) => (condition ? [element] : []);

      if (this.stats.edit.display === EDIT_DISPLAY.EMAIL) {
        this.$mixpanel.track('Outreach_Email', {
          account_id: this.$store.state.account.user.user.email,
          type: this.stats.edit.display,
          sent_to: this.stats.segment_name,
          click: this.stats.clicks,
        });
      } else if (this.stats.edit.display === EDIT_DISPLAY.LINK) {
        this.$mixpanel.track('Outreach_ShareLink', {
          account_id: this.$store.state.account.user.user.email,
          type: this.stats.edit.display,
          sent_to: this.stats.segment_name,
          click: this.stats.clicks,
        });
      } else if (this.stats.edit.display === EDIT_DISPLAY.PDF) {
        this.$mixpanel.track('Outreach_PDFdownload', {
          account_id: this.$store.state.account.user.user.email,
          type: this.stats.edit.display,
          sent_to: this.stats.segment_name,
          click: this.stats.clicks,
        });
      } else if (this.stats.edit.display === EDIT_DISPLAY.EMBED) {
        this.$mixpanel.track('Outreach_EmbedCode', {
          account_id: this.$store.state.account.user.user.email,
          type: this.stats.edit.display,
          sent_to: this.stats.segment_name,
          click: this.stats.clicks,
        });
      } else {
        this.$mixpanel.track('Outreach_QRcode', {
          account_id: this.$store.state.account.user.user.email,
          type: this.stats.edit.display,
          sent_to: this.stats.segment_name,
          click: this.stats.clicks,
        });
      }
      return [
        ...insertIf(this.display === EDIT_DISPLAY.EMAIL,
          {
            name: 'Sent to',
            value: this.stats.recipient_count,
          }),
        ...insertIf([EDIT_DISPLAY.EMAIL, EDIT_DISPLAY.LINK, EDIT_DISPLAY.WECHAT,
          EDIT_DISPLAY.EMBED].includes(this.display),
        {
          name: EMAIL_EVENTS.open,
          value: this.stats.opens,
        }),
        ...insertIf(Object.values(EDIT_DISPLAY).includes(this.display),
          {
            name: EMAIL_EVENTS.click,
            value: this.stats.clicks,
          }),
        ...insertIf(this.display === EDIT_DISPLAY.EMAIL,
          {
            name: EMAIL_EVENTS.unsubscribe,
            value: this.stats.unsubscribed_clients,
          })];
    },
    previewContent() {
      return this.$store.state.entries?.preview ?? null;
    },
  },
  watch: {
    stats(newVal) {
      if (newVal?.edit?.pk) {
        // Get preview contents
        this.$store.dispatch('entries/getPreview', { id: newVal.edit.pk, intro: false });
      }
    },
    // if customerId is defined, fetch customer data
    customerId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        // Fetch
        this.$store.dispatch('customers/fetchItem', { id: newVal, scope: 'stylist' });
      },
    },
  },
  mounted() {
    this.$store.dispatch('outreachs/fetchStats', { id: this.outreachId, customerId: this.customerId });
  },
  methods: {
    onActivityClicked(eventIndex) {
      const event = this.events[eventIndex];
      let eventKey = Object.entries(EMAIL_EVENTS).find(([key, val]) => val === event.name);
      eventKey = eventKey ? eventKey[0] : 'all_clients';
      console.log('clicked event key', eventKey);

      this.$mixpanel.track('click', {
        location: '/report/summary',
        account_id: this.$store.state.account.user.user.email,
        eventKey,
        outreach: this.outreachId,
      });

      const { customerId } = this.$route.params;
      this.$router.push(`${customerId ? `/clients/${customerId}` : '/reports'}/outreach/${
        this.outreachId}/activity/${eventKey}`);
    },
  },
};
</script>
<style lang="scss"
  scoped>
.main-widget {
  // Main container paddings
  padding: 0;

  ::v-deep>.sl-card>.card-body {
    padding: 24px 36px calc(84px - 25px) 36px;
  }

  .base-widget__content {
    >.sl-card {
      // vertical separation between sections
      padding-bottom: 25px;
    }

    :not(.no-border) {
      ::v-deep & .card-body {
        // Gray round card borders
        border: solid 1px #33333344;
      }
    }

    ::v-deep & .card-body {
      border-radius: 5px;
      overflow: hidden;
    }
  }
}

.no-border> ::v-deep .card-body {
  border: none !important;
}

.skeleton-preview{
  height: 500px;
  &:after{
    display: block;
    content: '';
    color: red;
    height: 300px;
    width: calc(100% + 20px);
    background: linear-gradient(0deg, rgba(255,255,255,1) 44%, rgba(255,255,255,0) 100%);
    position: relative;
    left: -10px;
    right: -10px;
    bottom: 320px;
  }
}
</style>
